// @flow
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import React, { Fragment } from 'react';
import Slider from 'react-slick';
import cx from 'classnames';
import PostCard from 'components/common/PostCard';
import type { PostItem } from 'store/modules/listing';
import FakePostCard from 'components/common/FakePostCard';
import ArrowRight from 'components/common/Arrow/ArrowRight';
import ArrowLeft from 'components/common/Arrow/ArrowLeft';
import './PostCardList.scss';

type Props = {
  posts?: ?(PostItem[]),
  loading: boolean,
  prefetching: boolean,
  width?: number,
  hasEnded: boolean,
  oneColumn?: boolean,
  placeholderCount?: number,
  hideUsername?: boolean,
  isTrending?: boolean,
};

const createArray = length => Array.from(Array(length).keys());
const getColumnCount = width => {
  const xWide = 1920;
  const wide = 1600;
  const xLarge = 1256;
  const large = 1024;
  const medium = 768;

  if (!width) return 1;

  if (width < medium) return 4;
  if (width < large) return 6;
  if (width < xLarge) return 6;
  if (width < wide) return 6;
  if (width < xWide) return 12;
  return 12;
};

const PrevArrow = props => {
  const { className, onClick } = props;
  return (
    <ArrowLeft
      classname={className}
      click={onClick}
      background={'white'}
      width={32}
      height={32}
      color={'#868e96'}
    />
  );
};

const NextArrow = props => {
  const { className, onClick } = props;

  return (
    <ArrowRight
      classname={className}
      click={onClick}
      background={'white'}
      width={32}
      height={32}
      color={'#868e96'}
    />
  );
};

const PostCardList = ({
  posts,
  loading,
  prefetching,
  width,
  hasEnded,
  oneColumn,
  placeholderCount,
  hideUsername,
  isTrending,
}: Props) => {
  const trendingCount = oneColumn ? 1 : getColumnCount(width);
  const fakePostList = createArray(trendingCount).map(num => (
    <FakePostCard key={num} oneColumn={oneColumn} isTrending={isTrending} />
  ));
  if (loading) {
    return (
      <div className={cx('PostCardList', 'is-not-trending')}>
        {fakePostList}
      </div>
    );
  }

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    slidesToShow: 5,
    slidesToScroll: 5,
    swipeToSlide: true,
    touchMove: true,
    responsive: [
      {
        breakpoint: 1920,
        settings: {
          slidesToShow: 5,
          slidesToScroll: 5,
        },
      },
      {
        breakpoint: 1600,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 4,
        },
      },
      {
        breakpoint: 1256,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
    ],
  };

  if (!posts) return null;
  const postList = posts.map(post => (
    <PostCard
      key={post.id}
      id={post.id}
      thumbnail={post.thumbnail}
      username={post.user.username}
      displayName={post.user.display_name}
      userThumbnail={post.user.thumbnail}
      title={post.title}
      body={(post.meta && post.meta.short_description) || post.body}
      date={post.released_at}
      urlSlug={post.url_slug}
      oneColumn={oneColumn}
      commentsCount={post.comments_count}
      isPrivate={post.is_private}
      hideUsername={hideUsername}
      isTrending={isTrending}
    />
  ));

  if (postList && postList.length === 0)
    return (
      <div className={cx('PostCardList', { 'is-not-trending': !isTrending })}>
        <div className="empty-list">아직 등록된 포스트가 없습니다.</div>
      </div>
    );

  return (
    <Fragment>
      <div className={cx('PostCardList', { 'is-not-trending': !isTrending })}>
        {isTrending ? (
          <Slider {...settings}>
            {postList}
          </Slider>
        ) : (
          <Fragment>
            {postList}
            {prefetching && fakePostList}
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

PostCardList.defaultProps = {
  posts: [],
  oneColumn: false,
  width: 0,
  placeholderCount: 12,
  hideUsername: false,
};

export default PostCardList;



// WEBPACK FOOTER //
// ./src/components/common/PostCardList/PostCardList.js